import { StorageHandler } from "@/lib/storage";
import { AvatarStateStorage, buildAvatarStateStorage } from "./avatarState";
import { TabStateStorage, buildTabStateStorage } from "./tabState";
import { TourStateStorage, buildTourStateStorage } from "./tourState";
import { UserStateStorage } from "./userState";
import { PreviewStateStorage, buildPreviewStateStorage } from "./previewState";
import { SessionStateStorage } from "./sessionState";
import { DebuggerStateStorage } from "./debuggerState";
import { ChecklistStateStorage } from "./checklistState";
export interface PersistentStorageParams {
  localStorageHandler: StorageHandler;
  sessionStorageHandler: StorageHandler;
}

export class PersistentStorage {
  private isPreview = false;

  userState!: UserStateStorage;
  tourState!: TourStateStorage;
  tabState!: TabStateStorage;
  sessionState!: SessionStateStorage;
  avatarState!: AvatarStateStorage;
  checklistState!: ChecklistStateStorage;
  debuggerState: DebuggerStateStorage;
  previewState: PreviewStateStorage;

  private explicitPreviewState?: {
    userState: UserStateStorage;
    tourState: TourStateStorage;
    tabState: TabStateStorage;
    avatarState: AvatarStateStorage;
    checklistState: ChecklistStateStorage;
  };

  private localStorageHandler: StorageHandler;
  private sessionStorageHandler: StorageHandler;

  constructor({ localStorageHandler, sessionStorageHandler }: PersistentStorageParams) {
    this.previewState = buildPreviewStateStorage(sessionStorageHandler);
    this.debuggerState = new DebuggerStateStorage(localStorageHandler);
    this.localStorageHandler = localStorageHandler;
    this.sessionStorageHandler = sessionStorageHandler;
    this.sessionState = new SessionStateStorage(this.isPreview, this.sessionStorageHandler);

    // Preview mode is disabled by default
    this.initializeStateStorages(this.isPreview);
    if (this.isPreview) {
      this.initializeNonPreviewStateStorages();
    }
  }

  enablePreviewMode() {
    this.isPreview = true;
    this.initializeStateStorages(this.isPreview);
  }

  private initializeStateStorages(isPreview: boolean) {
    const { userState, tourState, tabState, avatarState, checklistState } = this.createStateStorages(isPreview);
    this.userState = userState;
    this.tourState = tourState;
    this.tabState = tabState;
    this.avatarState = avatarState;
    this.checklistState = checklistState;
  }

  private initializeNonPreviewStateStorages() {
    const { userState, tourState, tabState, avatarState, checklistState } = this.createStateStorages(false);
    this.explicitPreviewState = {
      userState,
      tourState,
      tabState,
      avatarState,
      checklistState,
    };
  }

  private createStateStorages(isPreview: boolean) {
    return {
      userState: new UserStateStorage(isPreview, this.localStorageHandler),
      tourState: buildTourStateStorage(isPreview, this.localStorageHandler),
      tabState: buildTabStateStorage(isPreview, this.localStorageHandler, this.sessionStorageHandler),
      avatarState: buildAvatarStateStorage(isPreview, this.localStorageHandler),
      checklistState: new ChecklistStateStorage(isPreview, this.localStorageHandler),
    };
  }

  clearTourRelatedState() {
    this.tourState.clear();
    this.tabState.clear();
    this.avatarState.clear();
    this.userState.clear();
    this.checklistState.clear();
    if (this.explicitPreviewState) {
      this.explicitPreviewState.tourState.clear();
      this.explicitPreviewState.tabState.clear();
      this.explicitPreviewState.avatarState.clear();
      this.explicitPreviewState.userState.clear();
      this.explicitPreviewState.checklistState.clear();
    }
  }

  clearTourState() {
    this.tourState.clear();
    if (this.explicitPreviewState) {
      this.explicitPreviewState.tourState.clear();
    }
  }

  clearUserState() {
    this.userState.clear();
    if (this.explicitPreviewState) {
      this.explicitPreviewState.userState.clear();
    }
  }

  clearAll() {
    this.userState.clear();
    this.tourState.clear();
    this.tabState.clear();
    this.avatarState.clear();
    this.previewState.clear();
    this.checklistState.clear();

    if (this.explicitPreviewState) {
      this.explicitPreviewState.userState.clear();
      this.explicitPreviewState.tourState.clear();
      this.explicitPreviewState.tabState.clear();
      this.explicitPreviewState.avatarState.clear();
      this.explicitPreviewState.checklistState.clear();
    }
  }
}
